import { loadAllModules, loadEsriCss } from '../utils/esriLoader'
import store from '../../store/index.js'
import {
  addSelectedFeatures,
  removeSelectedFeatures,
  segmentSelectionStartUp,
  setMaploaded,
  setNotMatchingVariables,
  setPopupExpanded,
  setPopupSelectedFeature,
  setShareableURL,
  setShowCircularProgress,
  setShowErrAddingFeatureToProjectModal,
  setShowErrRemovingFeatureFromProjectModal,
  setShowLanduseErrModal,
  setShowNoFeatureSelectedModal,
  setShowToolbox,
  setToolboxExpanded
} from '../slices/appSlice.js'
import {
  appConfig,
  landUsePopupTemplate,
  landUsePopupTemplateAlreadyPartOfProject,
  landUsePopupTemplateDiffLanduse,
  landUsePopupTemplateDiffStName,
  landUsePopupTemplateDiffTiers,
  landUsePopupTemplateNotPartOfProject,
  landUsePopupTemplateRemoveFeature
} from '../config/appConfig'
import { ADD, REMOVE } from '../constants/segmentSelectionToolbox'

import { setDrawerOpen } from '../slices/mainTool'
import {
  ALREADY_PART_OF_PROJECT,
  DIFF_LANDUSE,
  DIFF_ST_NAME,
  DIFF_TIERS,
  NOT_PART_OF_PROJECT
} from '../constants/popup'
import {
  AUTO_LAYER,
  BIKE_LAYER,
  COUNTY_BOUNDARIES_LAYER,
  FREIGHT_LAYER,
  LANDUSE_CONTEXT,
  LANDUSE_LAYER,
  PEDESTRIAN_LAYER,
  ST_NAME,
  ST_TYPE,
  TRANSIT_LAYER
} from '../constants/layerFieldNames.js'
import { arraysAreEqual } from '../components/Mapview/MapView.jsx'

import {
  assignAutoPopup,
  assignBikePopup,
  assignFreightPopup,
  assignPedestrianPopup,
  assignTransitPopup
} from './controllerUtils.js'

let _esriModules = null
let _map = null
let _mapview = null
let _initialMiniExtent = null
let _handleMapviewClick = null
let _basemapGallery = null
let _legend = null
let _mapPoint = null
let _findCurrentLandUseContext = null
let _findCurrentStreetName = null
let _findCurrentStreetType = null
let _findCurrentStreetTierArr = null
let _landUseContextLayer = null
let _legendExpand = null
let _basemapGalleryExpand = null
export let mapImageLayer = null

let _leftPanelSearchWidget = null
export const loadEsriResources = async () => {
  loadEsriCss()
  const esriImports = [
    'esri/Map',
    'esri/views/MapView',
    'esri/config',
    'esri/geometry/Extent',
    'esri/layers/MapImageLayer',
    'esri/core/promiseUtils',
    'esri/Graphic',
    'esri/widgets/Search',
    'esri/widgets/Search/SearchSource',
    'esri/request',
    'esri/widgets/BasemapGallery',
    'esri/widgets/Expand',
    'esri/geometry/Polyline',
    'esri/widgets/Legend',
    'esri/core/reactiveUtils'
  ]
  _esriModules = await loadAllModules(esriImports)
  const { Map, MapView, Extent, BasemapGallery, Legend } = _esriModules
  _initialMiniExtent = new Extent({
    xmin: -9500818.934116973,
    ymin: 5094025.840920654,
    xmax: -9028389.236848848,
    ymax: 5357419.7985143615,
    spatialReference: {
      wkid: 102100
    }
  })

  _map = new Map({
    basemap: 'topo-vector'
  })

  _mapview = new MapView({
    map: _map,

    extent: _initialMiniExtent,
    zoom: 9
  })
  _basemapGallery = new BasemapGallery({
    view: _mapview,
    container: document.createElement('div')
  })
  _legend = new Legend({
    view: _mapview
  })
}
loadEsriResources()

// import esriConfig from '@arcgis/core/config'
export const initializeMap = async (domRef) => {
  await loadEsriResources()
  const { MapImageLayer, promiseUtils, Expand, Search, reactiveUtils } =
    _esriModules

  _mapview.container = domRef

  _leftPanelSearchWidget = new Search({
    view: _mapview,
    resultGraphicEnabled: true,
    locationEnabled: false,
    source: {
      url: 'https://geocode-api.arcgis.com/arcgis/rest/services/World/GeocodeServer',
      singleLineFieldName: 'SingleLine',
      name: 'ArcGIS World Geocoding Service',
      placeholder: 'Search Address..',
      maxResults: 3,
      maxSuggestions: 6,
      suggestionsEnabled: false,
      minSuggestCharacters: 0
    }
  })
  mapImageLayer = new MapImageLayer({
    url: appConfig.mapServerURL,
    outFields: ['*']
  })

  _basemapGallery.source.basemaps.items.push(_map.basemap)
  _basemapGalleryExpand = new Expand({
    view: _mapview,
    content: _basemapGallery,
    expandTooltip: 'Basemap Gallery',
    collapseTooltip: 'Basemap Gallery'
  })

  _mapview.ui.move(['zoom'], 'top-right')
  _mapview.ui.add(_basemapGalleryExpand, { position: 'top-right' })

  _legendExpand = new Expand({
    view: _mapview,
    content: _legend,
    expandTooltip: 'Map Legend',
    collapseTooltip: 'Map Legend'
  })
  _mapview.ui.add(_legendExpand, { position: 'top-right' })
  _map.add(mapImageLayer)
  _mapview.popup.dockOptions = {
    // Disable the dock button so users cannot undock the popup
    buttonEnabled: false
  }
  _mapview.popup.alignment = 'top-center'

  _mapview.whenLayerView(mapImageLayer).then(function () {
    mapImageLayer.sublayers.forEach(function (sublayer) {
      if (
        sublayer.title === LANDUSE_LAYER ||
        sublayer.title === COUNTY_BOUNDARIES_LAYER
      ) {
        sublayer.visible = true
      } else {
        sublayer.visible = false
      }

      if (sublayer.title === LANDUSE_LAYER) {
        sublayer
          .createFeatureLayer()
          .then((featureLayer) => featureLayer.load())
          .then((featureLayer) => {
            // Initialize landuse context layer
            _landUseContextLayer = sublayer
            _landUseContextLayer.outFields = '*'
            _landUseContextLayer.popupTemplate = landUsePopupTemplate
          })
      } else if (sublayer.title === PEDESTRIAN_LAYER) {
        // Set popup template for all Modal Network layers
        assignPedestrianPopup(sublayer)
      } else if (sublayer.title === BIKE_LAYER) {
        // Set popup template for all Modal Network layers
        assignBikePopup(sublayer)
      } else if (sublayer.title === FREIGHT_LAYER) {
        // Set popup template for all Modal Network layers
        assignFreightPopup(sublayer)
      } else if (sublayer.title === AUTO_LAYER) {
        assignAutoPopup(sublayer)
      } else if (sublayer.title === TRANSIT_LAYER) {
        // Set popup template for all Modal Network layers
        assignTransitPopup(sublayer)
      }
    })

    _mapview.on('immediate-click', (event) => _handleMapviewClick(event))
    // What for legend widget expand and collapse
    reactiveUtils.watch(
      () => _legendExpand?.expanded,
      (_legendExpanded) => {
        if (_legendExpanded) {
          // change Legend Title
          const legendTitle = document.querySelector('.esri-widget__heading')

          if (legendTitle) {
            legendTitle.innerHTML = 'Multimodal Tool Data'
          }
          // Hide toolbox
          store.dispatch(setShowToolbox(false))
          // Hide control panel
          store.dispatch(setDrawerOpen(false))
          // Hide basemap
          _basemapGalleryExpand.expanded = false
        } else {
          // change Legend Title
          const legendTitle = document.querySelector('.esri-widget__heading')

          if (legendTitle) {
            legendTitle.innerHTML = 'Multimodal Tool Data'
          }
        }
      }
    )
    // What for basemap widget expand and collapse
    reactiveUtils.watch(
      () => _basemapGalleryExpand?.expanded,
      (_basemapGalleryExpand) => {
        if (_basemapGalleryExpand) {
          // Hide toolbox
          store.dispatch(setShowToolbox(false))
          // Hide control panel
          store.dispatch(setDrawerOpen(false))
          // Hide Legend
          _legendExpand.expanded = false
        }
      }
    )
    store.dispatch(setMaploaded(true))
  })

  // What selectedFeature for popup chnage update button text
  reactiveUtils.watch(
    () => _mapview?.popup.selectedFeature,
    (selectedFeature) => {
      if (selectedFeature) {
        store.dispatch(setPopupSelectedFeature(selectedFeature))
      }
    }
  )

  // What popup is visible , collapse custompopup
  reactiveUtils.watch(
    () => _mapview?.popup.visible,
    (popupVisible) => {
      if (popupVisible) {
        store.dispatch(setPopupExpanded(false))
        store.dispatch(setToolboxExpanded(false))
        // clsoe drawer
        store.dispatch(setDrawerOpen(false))
      } else {
        store.dispatch(setPopupExpanded(true))
        store.dispatch(setToolboxExpanded(true))
        // clsoe drawer
        store.dispatch(setDrawerOpen(false))
      }
    }
  )

  _handleMapviewClick = (event) => {
    _mapPoint = event.mapPoint
    // Check if the zoom level is aboeve 15 and zoom into the mapview
    if (_mapview.zoom < 10) {
      _mapview.goTo(
        {
          center: _mapPoint,
          zoom: 13
        },
        { duration: 1000 }
      )
    }

    // const query = {
    //   geometry: event.mapPoint,
    //   distance: 0.1,
    //   units: 'miles',
    //   spatialRelationship: 'intersects',
    //   returnGeometry: true,
    //   outFields: ['*']
    // }
    // debouncedRunQuery().catch(function (err) {
    //   if (!promiseUtils.isAbortError(err)) {
    //     throw err
    //   }
    // })
  }
  const debouncedRunQuery = promiseUtils.debounce(function () {
    mapImageLayer.sublayers.forEach(function (sublayer) {
      if (sublayer.title === LANDUSE_LAYER) {
        _landUseContextLayer = sublayer
        // runLayerQuery(sublayer)
      }
    })
  })

  return () => {
    _mapview.container = null
  }
}

export const toggleErrPopupTemplates = (errState) => {
  // toggle popup templates, based on error state
  if (_landUseContextLayer) {
    switch (errState) {
      case ALREADY_PART_OF_PROJECT:
        _landUseContextLayer.popupTemplate =
          landUsePopupTemplateAlreadyPartOfProject
        break
      case DIFF_ST_NAME:
        _landUseContextLayer.popupTemplate = landUsePopupTemplateDiffStName
        break
      case DIFF_LANDUSE:
        _landUseContextLayer.popupTemplate = landUsePopupTemplateDiffLanduse
        break
      case DIFF_TIERS:
        _landUseContextLayer.popupTemplate = landUsePopupTemplateDiffTiers
        break
      case NOT_PART_OF_PROJECT:
        _landUseContextLayer.popupTemplate =
          landUsePopupTemplateNotPartOfProject
        break
      default:
    }
  }
}

export const togglePopupTemplates = (activeTool) => {
  // toggle popup templates, based on add or remove feature
  if (_landUseContextLayer) {
    if (activeTool === ADD) {
      _landUseContextLayer.popupTemplate = landUsePopupTemplate
    } else if (activeTool === REMOVE) {
      _landUseContextLayer.popupTemplate = landUsePopupTemplateRemoveFeature
    }
  }
}

export const selectFeatureFromMap = () => {
  const state = store.getState()
  if (state.segmentSelectionToolbox.activeTool === ADD) {
    //  Once you've activated manual selection and clicked the map, reset query graphics.
    // _mapview.graphics.removeAll()

    // If there are no features in the mapPoint ,return.
    if (!_mapview.popup.selectedFeature) {
      _mapview.popup.close()
      // show no feature selected modal
      store.dispatch(setShowNoFeatureSelectedModal(true))
      return
    }

    // Check if the selected Feature is already part of the redux state
    const info = segmentSelectionStartUp(
      { selectedFeaturesArr: state.appSlice.selectedFeaturesArr },
      { payload: _mapview.popup.selectedFeature }
    )

    const segmentsToAdd = !info.currentSegmentIds.has(info.incomingSegmentId)
      ? [info.incomingSegment]
      : []

    // If the incoming feature is already part of the redux state, then return.
    if (!segmentsToAdd.length) {
      store.dispatch(setShowErrAddingFeatureToProjectModal(true))
      return
    }

    const notMatchingVariables = []

    const currentLanduseContext = _findCurrentLandUseContext()
    // If incoming parcels do not have the same landuse as the existing selection, show a modal.
    const firstSegmentLanduseContext =
      _mapview.popup.selectedFeature.attributes[LANDUSE_CONTEXT]

    const currentStreetName = _findCurrentStreetName()
    // If incoming parcels do not have the same st.Name as the existing selection, show a modal.
    const firstSegmentStreetName =
      _mapview.popup.selectedFeature.attributes[ST_NAME]

    // If incoming parcels do not have the same St.Type as the existing selection, show a modal.
    const firstSegmentStreetType =
      _mapview.popup.selectedFeature.attributes[ST_TYPE]

    const currentStreetTierArr = _findCurrentStreetTierArr()
    const streetTierArr = [
      'Ped_Tier',
      'Bike_Tier',
      'Transit_Tier',
      'Auto_Tier',
      'Freight_Tier'
    ]
    const firstSegmentStTierArr = streetTierArr.map((tier) => {
      return _mapview.popup.selectedFeature.attributes[tier]
    })

    // Check if the incoming street is of same Landuse context, street name , type as the current selection.
    if (state.appSlice.selectedFeaturesArr.length > 0) {
      // Check for landuseContext
      if (firstSegmentLanduseContext !== currentLanduseContext) {
        notMatchingVariables.push(
          `Landuse Context (${firstSegmentLanduseContext})`
        )
      }
      // Check for St Name
      if (firstSegmentStreetName !== currentStreetName) {
        notMatchingVariables.push(
          `Street Name (${firstSegmentStreetName} ${firstSegmentStreetType})`
        )
      }
      // Check for tiers
      if (
        arraysAreEqual(currentStreetTierArr, firstSegmentStTierArr) === false
      ) {
        notMatchingVariables.push('Street Tiers')
      }
    }
    // set notMatching variables in redux state
    store.dispatch(setNotMatchingVariables(notMatchingVariables))

    // If not matching variables length does not exsist then add segmet
    // If the incoming segment has the same Land use Context, same name and tye then add segment
    if (!notMatchingVariables.length) {
      if (state.appSlice.shareableURL) {
        store.dispatch(setShareableURL(false))
      }
      // Keep toolbox open , whenever user clicks on map
      // store.dispatch(setShowToolbox(true))
      store.dispatch(addSelectedFeatures(_mapview.popup.selectedFeature))
      // Once feature has been added to porject close popup
      _mapview.popup.close()
    } else {
      store.dispatch(setShowLanduseErrModal(true))
    }
  }
  if (state.segmentSelectionToolbox.activeTool === REMOVE) {
    // Check if the selected Feature is already part of the redux state
    const info = segmentSelectionStartUp(
      { selectedFeaturesArr: state.appSlice.selectedFeaturesArr },
      { payload: _mapview.popup.selectedFeature }
    )

    const segmentsToAdd = !info.currentSegmentIds.has(info.incomingSegmentId)
      ? [info.incomingSegment]
      : []

    // If the incoming feature is not already part of the redux state, then return and show error modal.
    if (segmentsToAdd.length) {
      store.dispatch(setShowErrRemovingFeatureFromProjectModal(true))
      return
    }
    store.dispatch(removeSelectedFeatures(_mapview.popup.selectedFeature))
    // Close popup once feature has been removed from project
    _mapview.popup.close()
  }
}

export const attachLeftPanelSearchWidget = (container) => {
  _leftPanelSearchWidget.container = container
}

export const collapseLegendAndBaseMap = () => {
  _legendExpand.expanded = false
  _basemapGalleryExpand.expanded = false
}

const controller = new AbortController()
const lineSymbol = {
  type: 'simple-line',
  color: '#01FFFF',
  outline: '10px',
  width: 5
  // style: 'short-dot'
}
export const setMapviewExtent = () => {
  _mapview.graphics.removeAll()
  const { Polyline } = _esriModules
  const { selectedFeaturesArr } = store.getState().appSlice
  if (selectedFeaturesArr.length) {
    let paths = []
    selectedFeaturesArr.forEach((feature) => {
      paths = [...paths, ...feature.geometry.paths]
    })
    const line = new Polyline({
      hasZ: false,
      hasM: true,
      paths,
      spatialReference: { wkid: 102100 }
    })
    // Add graphic to the map
    updateSelectedSegmentGraphics(selectedFeaturesArr)
    // _mapview.extent = line.extent
    // _mapview.zoom = 16
    _mapview.goTo(
      {
        target: line,
        zoom: 16
      },
      { duration: 1000 }
    )
    _mapview.padding = { bottom: 500 }
  }
}

export const zoomToSelectedFeature = () => {
  const { Polyline } = _esriModules
  const { selectedFeaturesArr } = store.getState().appSlice
  let paths = []
  selectedFeaturesArr.forEach((feature) => {
    paths = [...paths, ...feature.geometry.paths]
  })
  const line = new Polyline({
    hasZ: false,
    hasM: true,
    paths,
    spatialReference: { wkid: 102100 }
  })
  _mapview.goTo(
    {
      target: line,
      zoom: 16
    },
    { duration: 1000 }
  )
  _mapview.padding = { bottom: 500 }
  _mapview.popup.close()
}

export const removeSelectedSegment = () => {
  _mapview.graphics?.removeAll()
}
export const togglePopup = (isCollapsed) => {
  _mapview.when(() => {
    _mapview.popup.collapsed = isCollapsed
  })
}
_findCurrentLandUseContext = () => {
  const state = store.getState()
  // If the user has already selected a parcel, set that parcel's census tract as the currentCensusTract.
  const landuseContext =
    state.appSlice.selectedFeaturesArr.length > 0
      ? state.appSlice.selectedFeaturesArr[0].attributes.Context2
      : _mapview.popup.selectedFeature.attributes.Context2
  return landuseContext
}
_findCurrentStreetName = () => {
  const state = store.getState()
  // If the user has already selected a parcel, set that parcel's census tract as the currentCensusTract.
  const streetName =
    state.appSlice.selectedFeaturesArr.length > 0
      ? state.appSlice.selectedFeaturesArr[0].attributes[ST_NAME]
      : _mapview.popup.selectedFeature.attributes[ST_NAME]
  return streetName
}

_findCurrentStreetType = (featureSet) => {
  const state = store.getState()
  // If the user has already selected a parcel, set that parcel's census tract as the currentCensusTract.
  const streetType =
    state.appSlice.selectedFeaturesArr.length > 0
      ? state.appSlice.selectedFeaturesArr[0].attributes[ST_TYPE]
      : featureSet.features.map((feat) => feat.attributes[ST_TYPE])[0]
  return streetType
}
_findCurrentStreetTierArr = () => {
  const state = store.getState()
  const streetTierArr = [
    'Ped_Tier',
    'Bike_Tier',
    'Transit_Tier',
    'Auto_Tier',
    'Freight_Tier'
  ]
  const stTierArr = streetTierArr.map((tier) => {
    return state.appSlice.selectedFeaturesArr.length > 0
      ? state.appSlice.selectedFeaturesArr[0].attributes[tier]
      : _mapview.popup.selectedFeature.attributes[tier]
  })
  return stTierArr
}
function runLayerQuery(sublayer) {
  const query = sublayer.createQuery()
  query.geometry = _mapPoint
  query.returnGeometry = true
  query.outFields = ['*']
  query.spatialReference = { wkid: 102100 }
  query.distance = 10
  query.units = 'feet'
  // query.layer = sublayer

  const state = store.getState()
  if (state.segmentSelectionToolbox.activeTool === ADD) {
    //  Once you've activated manual selection and clicked the map, reset query graphics.
    // _mapview.graphics.removeAll()
    sublayer.queryFeatures(query).then((featureSet) => {
      // If there are no features in the mapPoint ,return.
      if (!featureSet.features.length) {
        _mapview.popup.close()
        // show no feature selected modal
        store.dispatch(setShowNoFeatureSelectedModal(true))
        return
      }
      const notMatchingVariables = []
      // Check if the first selected segment has the same Land use Context, street name and type.
      if (!state.appSlice.selectedFeaturesArr.length) {
        const firstSegmentName = featureSet.features[0].attributes[ST_NAME]
        const firstSegmentLanduseContext =
          featureSet.features[0].attributes[LANDUSE_CONTEXT]
        // filter out all the features which only have the same name as the first segment.
        const allIncomingStNameSame = featureSet.features.every(
          (feature) => feature.attributes[ST_NAME] === firstSegmentName
        )
        // Only if the selected features have same name and landuse context then allow graphics to be highlightes, ptherwise return
        if (!allIncomingStNameSame) {
          console.log('St name not same', featureSet)
          notMatchingVariables.push('Street Name')
          // set notMatching variables in redux state
          store.dispatch(setNotMatchingVariables(notMatchingVariables))
          store.dispatch(setShowLanduseErrModal(true))
          return
        }
        const allIncomingLanduseContextSame = featureSet.features.every(
          (feature) =>
            feature.attributes.Context2 === firstSegmentLanduseContext
        )

        // Only if the selected features have same name and landuse context then allow graphics to be highlightes, ptherwise return

        if (!allIncomingLanduseContextSame) {
          console.log('Landuse context not same', featureSet)
          notMatchingVariables.push('Landuse context')
          // set notMatching variables in redux state
          store.dispatch(setNotMatchingVariables(notMatchingVariables))
          store.dispatch(setShowLanduseErrModal(true))
          return
        }
      }

      const currentLanduseContext = _findCurrentLandUseContext(featureSet)
      // If incoming parcels do not have the same census tract as the existing selection, show a modal.
      const firstSegmentLanduseContext = featureSet.features.map(
        (feat) => feat.attributes[LANDUSE_CONTEXT]
      )[0]
      const allIncomingLanduseContextSame = featureSet.features.every(
        (feature) =>
          feature.attributes[LANDUSE_CONTEXT] === firstSegmentLanduseContext
      )

      const currentStreetName = _findCurrentStreetName(featureSet)
      // If incoming parcels do not have the same census tract as the existing selection, show a modal.
      const firstSegmentStreetName = featureSet.features.map(
        (feat) => feat.attributes[ST_NAME]
      )[0]
      const allIncomingStreetNameSame = featureSet.features.every(
        (feature) => feature.attributes[ST_NAME] === firstSegmentStreetName
      )

      const currentStreetType = _findCurrentStreetType(featureSet)
      // If incoming parcels do not have the same census tract as the existing selection, show a modal.
      const firstSegmentStreetType = featureSet.features.map(
        (feat) => feat.attributes[ST_TYPE]
      )[0]
      const allIncomingStreetTypeSame = featureSet.features.every(
        (feature) => feature.attributes[ST_TYPE] === firstSegmentStreetType
      )

      // Check if the incoming street is of same Landuse context, street name , type as the current selection.
      if (state.appSlice.selectedFeaturesArr.length > 0) {
        // Check for landuseContext
        if (
          !allIncomingLanduseContextSame ||
          firstSegmentLanduseContext !== currentLanduseContext
        ) {
          notMatchingVariables.push(
            `Landuse Context (${firstSegmentLanduseContext})`
          )
        }
        // Check for St Name
        if (
          !allIncomingStreetNameSame ||
          firstSegmentStreetName !== currentStreetName
        ) {
          notMatchingVariables.push(
            `Street Name (${firstSegmentStreetName} ${firstSegmentStreetType})`
          )
        }
        // Check for landuseContext
        // if (
        //   !allIncomingStreetTypeSame ||
        //   firstSegmentStreetType !== currentStreetType
        // ) {
        //   console.log('type no match', featureSet.features)
        //   notMatchingVariables.push('Street Type')
        // }
      }
      // set notMatching variables in redux state
      store.dispatch(setNotMatchingVariables(notMatchingVariables))

      // If not matching variables length does not exsist then add segmet
      // If the incoming segment has the same Land use Context, same name and tye then add segment
      if (!notMatchingVariables.length) {
        if (state.appSlice.shareableURL) {
          store.dispatch(setShareableURL(false))
        }
        // Keep toolbox open , whenever user clicks on map
        store.dispatch(setShowToolbox(true))
        store.dispatch(addSelectedFeatures(featureSet))
      } else {
        store.dispatch(setShowLanduseErrModal(true))
      }
    })
  }
  if (state.segmentSelectionToolbox.activeTool === REMOVE) {
    sublayer.queryFeatures(query).then((featureSet) => {
      store.dispatch(removeSelectedFeatures(featureSet))
    })
  }
}

export const toggleLayerVisibility = (id, visibility) => {
  // eslint-disable-next-line no-restricted-globals
  store.dispatch(setShowCircularProgress(true))
  const sublayer = mapImageLayer.findSublayerById(Number.parseInt(id))
  sublayer.visible = visibility

  // Show activity inidicator for 5 sec,
  setTimeout(
    () => {
      store.dispatch(setShowCircularProgress(false))
    },
    visibility ? 4000 : 1000
  )
}

export const getOIDField = () => {
  const oidField = _landUseContextLayer?.fields?.find((field) => {
    return field.type === 'oid'
  })
  return oidField?.name
}

export const updateSelectedSegmentGraphics = (segments) => {
  _mapview.graphics.removeAll()
  if (segments.length) {
    const { Graphic, Polyline } = _esriModules
    let paths = []
    segments.forEach((feature) => {
      paths = [...paths, ...feature.geometry.paths]
    })
    const line = new Polyline({
      hasZ: false,
      hasM: true,
      paths,
      spatialReference: { wkid: 102100 }
    })

    const lineGraphic = new Graphic({
      geometry: line,
      symbol: lineSymbol
    })

    _mapview.graphics.add(lineGraphic)
  }
}
